import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'curx_theme';
export const THEMES = ['light', 'dark', 'system'];

function readStored() {
  if (typeof window === 'undefined') return 'system';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(v) ? v : 'system';
  } catch (_) {
    return 'system';
  }
}

function resolveSystem() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * useTheme — Phase 12.
 * Returns the current theme preference (`light` | `dark` | `system`)
 * and the resolved theme actually applied (`light` | `dark`),
 * plus a setter that persists to localStorage and updates the DOM.
 *
 * Initial DOM apply is also done by an inline script in
 * public/index.html so first paint matches the user's preference
 * (no FOUC). This hook keeps everything in sync afterwards.
 */
export function useTheme() {
  const [theme, setThemeState] = useState(readStored);
  const [resolved, setResolved] = useState(() =>
    readStored() === 'system' ? resolveSystem() : readStored()
  );

  const apply = useCallback((value) => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const r = value === 'system' ? resolveSystem() : value;
    root.setAttribute('data-theme', r);
    setResolved(r);
  }, []);

  // Apply on mount and whenever theme changes.
  useEffect(() => {
    apply(theme);
  }, [theme, apply]);

  // While theme === 'system', listen for OS-level changes.
  useEffect(() => {
    if (theme !== 'system') return undefined;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply('system');
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Older Safari fallback:
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [theme, apply]);

  const setTheme = useCallback((value) => {
    if (!THEMES.includes(value)) return;
    try { window.localStorage.setItem(STORAGE_KEY, value); } catch (_) { /* private mode */ }
    setThemeState(value);
  }, []);

  return { theme, resolvedTheme: resolved, setTheme };
}
